import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../../theme';
import Disclaimer from '../../../Disclaimer/Disclaimer';
import { InfoIcon } from '../../../FlightCard/components/Icons';
import { useConfig } from '../../../context';
import {
	BaggageFragment,
	CheckinPassengerFragment,
	GdAdditionalParameterFragment,
	InputGdsServiceParameter,
	PassengerFragment,
	SegmentFragment
} from '@websky/graphql';
import { SegmentService, SelectedService, useAnimalState } from './hooks';
import AdditionalServiceWrapper from '../../../AdditionalServiceWrapper';
import Control from '../Control/Control';
import { getTravellerName, isExistI18nTranslate } from '../../../utils';
import { Form } from 'react-final-form';
import PassengerForm from './PassengerForm/PassengerForm';

export interface Props {
	segments: SegmentFragment[];
	services: BaggageFragment[];
	travellers: (PassengerFragment | CheckinPassengerFragment)[];
	isLoading?: boolean;
	onConfirm: (selectedServices: SelectedService[]) => void;
	onClear: (selectedServices: SelectedService[]) => void;
	onClose?: () => void;
	readOnly?: boolean;
	aggregationMode?: boolean;
}

const PetPopup: React.FC<Props> = ({
	segments,
	services,
	travellers,
	isLoading,
	onConfirm,
	onClear,
	onClose,
	readOnly,
	aggregationMode
}) => {
	const { PetPopup: css } = useTheme('CheckoutAdditionalService');
	const { t } = useTranslation('Checkout');

	const baggageAnimalsURL = useConfig()?.global.companyInfo?.baggageAnimalsURL;

	const {
		state,
		servicesBySegment,
		selectedServices,
		servicesToSave,
		hasChanges,
		initialState,
		totalPrice,
		onChange,
		onClear: onClearState
	} = useAnimalState(services, segments, travellers);

	const onSaveHandler = (values: any) => {
		if (hasChanges) {
			onConfirm(
				servicesToSave.map(selectedServices => {
					const formData =
						values['passengers']?.[selectedServices.passengerId]?.[selectedServices.segmentId] ?? {};

					const extraValues: InputGdsServiceParameter[] = servicesBySegment
						.find(service => selectedServices.segmentId === service[1].segmentId)?.[1]
						?.additionalFields?.map(field => ({
							type: field.type,
							name: field.name,
							value: formData[field.name] ?? ''
						}));

					return { ...selectedServices, extraValues };
				})
			);
		} else {
			onClose?.();
		}
	};

	const onClearHandler = useCallback(() => {
		onClear(selectedServices.map(service => ({ ...service, count: 0 })));
		onClearState();
	}, [onClear, selectedServices]);

	const onChangeHandler = useCallback(
		(service: SegmentService) => (travellerId: string, checked: boolean) => {
			onChange({
				travellerId,
				segmentId: service.segmentId,
				segmentIds: service.segmentIds,
				serviceId: checked ? service.serviceId : null
			});
		},
		[servicesBySegment]
	);

	const renderOptionForm = (
		segmentId: string,
		passengerId: string,
		additionalField: GdAdditionalParameterFragment[]
	) => {
		if (!state[segmentId][passengerId] || initialState[segmentId][passengerId] || !additionalField) {
			return null;
		}

		return <PassengerForm passengerId={+passengerId} segmentId={+segmentId} fields={additionalField} />;
	};

	return (
		<Form onSubmit={onSaveHandler}>
			{({ handleSubmit }) => (
				<form className={css.form} onSubmit={handleSubmit}>
					<AdditionalServiceWrapper
						className={aggregationMode && css.content_aggregator}
						headerClassName={css.header}
						wrapperClassName={css.content}
						header={t('Animal')}
						text={
							<>
								<ul className={css.terms}>
									{[...Array(10)]
										.filter((_, index) =>
											isExistI18nTranslate(`PetPopupRule_${index}`, { ns: 'Checkout' })
										)
										.map((_, index) => (
											<li key={index}>{t(`PetPopupRule_${index}`)}</li>
										))}
								</ul>
								{baggageAnimalsURL && (
									<a href={baggageAnimalsURL} target="_blank">
										<Disclaimer
											className={css.disclaimer}
											wrapperClassName={css.disclaimer__wrapper}
											icon={InfoIcon}
										>
											{t('Open the full text of the conditions of carriage')}
										</Disclaimer>
									</a>
								)}
							</>
						}
						totalPrice={totalPrice}
						isLoading={isLoading}
						onClear={onClearHandler}
						onCancel={onClose}
						readOnly={readOnly}
						submit={true}
					>
						<div className={css.segments}>
							{servicesBySegment.map(([direction, service], index) => {
								const segmentId = service.segmentId;
								const serviceId = service.serviceId;
								const segmentIds = service.segmentIds;

								const allowedPassengersSet = new Set(
									service.service.allowedPassengers.reduce((acc, ids) => [...acc, ...ids], [])
								);

								const serviceTravellers = travellers.filter(traveller =>
									allowedPassengersSet.has(traveller.id)
								);

								let price = service.price;

								if (segmentIds.length > 1) {
									price = {
										...price,
										amount: service.price.amount * segmentIds.length
									};
								}

								return (
									<Control
										key={index}
										type="checkbox"
										info={direction}
										date={service.date}
										optionClassName={css.option}
										renderOptionPayload={option =>
											renderOptionForm(segmentId, option.id, service.additionalFields)
										}
										options={serviceTravellers.map(traveller => {
											const isSelected = state[segmentId][traveller.id] === serviceId;

											const confirmedCount =
												traveller.services?.gdsServices?.services?.find(
													selectedService =>
														selectedService.serviceId === serviceId &&
														selectedService.segmentIds.includes(segmentId)
												)?.confirmedCount ?? 0;

											const isCanModify =
												service.service.canBeAdded || (isSelected && !confirmedCount);

											const selectedButDisabled = confirmedCount > 0 && !isCanModify;

											return {
												id: traveller.id,
												label: getTravellerName(traveller).toLowerCase(),
												value: isSelected || selectedButDisabled ? 'selected' : undefined,
												disabled: isLoading || confirmedCount > 0 || !isCanModify,
												price
											};
										})}
										onChange={onChangeHandler(service)}
										isDisabled={isLoading}
									/>
								);
							})}
						</div>
					</AdditionalServiceWrapper>
				</form>
			)}
		</Form>
	);
};

export default PetPopup;
